/**
 * "Paid in full correction" from the Finance dossier (Josie Evans, 2026-10-21: "I have a couple of patients who need a paid
 * in full correction - I am unable to do this on the finance side").
 *
 * The account was marked paid in full, and a BOM/POA later shows FLMMIS did not reinstate the whole period, so Gwynn must
 * submit it to DCF again. Finance states why, may leave instructions for Gwynn, and confirms; the server starts UMED's
 * PAID_IN_FULL_CORRECTION on this resident's case (the type is the server's), prepares the corrected UMED and tells Gwynn.
 * It changes nothing in Finance's own ledger, and not what "Paid in full" or "Paid in full, UMED pending" do.
 *
 * Shaped like the paid-in-full mark's form (umed/PaidInFullMark): the reason first, the confirm button disabled until it
 * is at least 3 characters, one Idempotency-Key per opening so a retry of the same click answers the same correction.
 */
import React, { useEffect, useRef, useState } from 'react';
import { financeApi, outcomeOf, freshKey, FinanceCorrectionType } from './financeApi';
import { CORRECTION_TYPE_OPTIONS } from './paidInFullOptions';
import MonthsNotReflecting from '../../components/corrections/MonthsNotReflecting';

export const CORRECTION_DONE = 'Gwynn has been told. The UMED packet is being prepared.';
export const CORRECTION_OPEN_NOTE = 'A correction is already open on this case; complete it before requesting another.';
export const CORRECTION_RESUBMITTAL_NOTE = 'A DCF resubmittal is open on this case; finish or withdraw it before raising a correction.';
const REASON_MAX = 2000;
const NOTE_MAX = 2000;

/** Why a new Paid in full correction is not offered on this case, in words; null when it may be. The server decides again. */
export function CORRECTION_NOT_OFFERED(umed: { stage: string; correction_open?: boolean; resubmittal_open?: boolean }): string | null {
  if (umed.stage === 'CLOSED') return 'This case is closed.';
  if (umed.correction_open) return CORRECTION_OPEN_NOTE;
  if (umed.resubmittal_open) return CORRECTION_RESUBMITTAL_NOTE;
  return null;
}

export default function PaidInFullCorrectionForm({ patientId, caseId, name, effect, onClose, onDone }: {
  patientId: string; caseId: string; name: string; effect: string; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(freshKey());
  const first = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => { first.current?.focus({ preventScroll: true }); }, []);
  const ok = reason.trim().length >= 3;
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await financeApi.requestPaidInFullCorrection(patientId, caseId, {
        reason: reason.trim(), ...(note.trim() ? { note_to_gwynn: note.trim() } : {}), idempotency_key: key.current,
      });
      if (!r.success) { setErr(outcomeOf(r, { wrote: true })); return; }
      key.current = freshKey();
      onDone(CORRECTION_DONE);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-form um-rowpanel" role="group" aria-label={`Paid in full correction for ${name}`} data-testid="pif-correction-form" onClick={(e) => e.stopPropagation()}
      onKeyDown={busy ? undefined : (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}>
      <p className="um-rowpanel-what" data-testid="pif-correction-effect">{effect} Gwynn is told, and the corrected UMED is prepared for DCF.</p>
      <label className="um-rowfield"><span>Why (required)</span>
        <textarea ref={first} rows={3} maxLength={REASON_MAX} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}
          aria-label="Why the paid-in-full correction is needed" /></label>
      <label className="um-rowfield"><span>Instructions for Gwynn (optional)</span>
        <textarea rows={2} maxLength={NOTE_MAX} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)}
          aria-label="Instructions for Gwynn (optional)" /></label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="btns um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ok} onClick={run}>{busy ? 'Requesting…' : 'Request the correction'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * Start a correction from the Finance dossier, choosing its TYPE (Josie Evans, CORRECTIONS, 2026-10-28, R3: "Josie and Dom
 * must be able to choose the TYPE of correction"). Two choices, each with its one-line definition; an SFS correction may
 * name the months that do not reflect ('YYYY-MM', as a partial approval names them). The server maps the choice through
 * the same two-entry allowlist and tells Gwynn; Finance's own handoff is not moved. `initialType` preselects one.
 */
export function CorrectionStartForm({ patientId, caseId, name, initialType = null, onClose, onDone }: {
  patientId: string; caseId: string; name: string; initialType?: FinanceCorrectionType | null; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [type, setType] = useState<FinanceCorrectionType | null>(initialType);
  const [months, setMonths] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(freshKey());
  const chosen = CORRECTION_TYPE_OPTIONS.find((o) => o.type === type) || null;
  const ok = Boolean(chosen) && reason.trim().length >= 3;
  const run = async () => {
    if (!chosen) return;
    setBusy(true); setErr(null);
    try {
      const r = await financeApi.requestCorrection(patientId, caseId, {
        correction_type: chosen.type, reason: reason.trim(), ...(note.trim() ? { note_to_gwynn: note.trim() } : {}),
        ...(chosen.months && months.length ? { months } : {}), idempotency_key: key.current,
      });
      if (!r.success) { setErr(outcomeOf(r, { wrote: true })); return; }
      key.current = freshKey();
      onDone(CORRECTION_DONE);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-form um-rowpanel" role="group" aria-label={`Start a correction for ${name}`} data-testid="correction-start-form" onClick={(e) => e.stopPropagation()}
      onKeyDown={busy ? undefined : (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}>
      <fieldset className="um-rowfield fn-correction-types" disabled={busy}>
        <legend>Which correction?</legend>
        {CORRECTION_TYPE_OPTIONS.map((o) => (
          <label key={o.type} className="fn-correction-type" data-testid={`correction-type-${o.type}`}>
            <input type="radio" name={`correction-type-${caseId}`} value={o.type} checked={type === o.type}
              onChange={() => { setType(o.type); key.current = freshKey(); }} />
            <span><strong>{o.label}</strong> · {o.what}</span>
          </label>
        ))}
      </fieldset>
      {chosen && <p className="um-rowpanel-what" data-testid="correction-effect">{chosen.effect}</p>}
      {chosen && chosen.months && (
        <MonthsNotReflecting months={months} onChange={setMonths} disabled={busy} />
      )}
      <label className="um-rowfield"><span>Why (required)</span>
        <textarea rows={3} maxLength={REASON_MAX} value={reason} disabled={busy || !chosen} onChange={(e) => setReason(e.target.value)}
          aria-label="Why the correction is needed" /></label>
      <label className="um-rowfield"><span>Instructions for Gwynn (optional)</span>
        <textarea rows={2} maxLength={NOTE_MAX} value={note} disabled={busy || !chosen} onChange={(e) => setNote(e.target.value)}
          aria-label="Instructions for Gwynn (optional)" /></label>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="btns um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || !ok} onClick={run}>{busy ? 'Requesting…' : 'Request the correction'}</button>
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
